import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Phone, User, HardHat } from "lucide-react";

const STATUSES = ["CREATED", "ACKNOWLEDGED", "CONTACTING", "ESCALATED", "INVESTIGATING", "RESOLVED", "CLOSED_WITH_NOTES"];
const SEV = { emergency: "bg-red-600 text-white", high: "bg-red-50 text-red-600", medium: "bg-amber-50 text-amber-700", low: "bg-slate-100 text-slate-600" };
const SSC = { CREATED: "bg-red-50 text-red-600", ACKNOWLEDGED: "bg-amber-50 text-amber-700", CONTACTING: "bg-blue-50 text-blue-700", ESCALATED: "bg-red-100 text-red-700", INVESTIGATING: "bg-indigo-50 text-indigo-700", RESOLVED: "bg-emerald-50 text-emerald-700", CLOSED_WITH_NOTES: "bg-slate-100 text-slate-600" };

export default function AdminSafety() {
    const [rows, setRows] = useState(null);
    const load = () => api.get("/admin/safety-cases").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const open = rows?.filter((r) => !["RESOLVED", "CLOSED_WITH_NOTES"].includes(r.status)) || [];

    return (
        <div>
            <div className="flex items-center gap-2"><ShieldAlert size={22} className="text-red-500" /><h1 className="font-heading text-2xl font-extrabold text-navy">Safety & Emergency</h1></div>
            <p className="text-sm text-navy/55">{open.length} open · {rows?.length || 0} total cases</p>
            {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-red-500" /></div>}
            <div className="mt-4 space-y-3">
                {rows?.map((c) => <CaseCard key={c.id} c={c} onSaved={load} />)}
                {rows && rows.length === 0 && <div className="rounded-2xl border border-dashed border-navy/15 py-12 text-center text-sm text-navy/40">No safety cases reported.</div>}
            </div>
        </div>
    );
}

function CaseCard({ c, onSaved }) {
    const [status, setStatus] = useState(c.status);
    const [support, setSupport] = useState(c.assigned_support || "");
    const [note, setNote] = useState("");
    const [outcome, setOutcome] = useState(c.outcome || "");
    const [busy, setBusy] = useState(false);
    const emergency = c.severity === "emergency";

    const save = async () => {
        setBusy(true);
        try { await api.patch(`/admin/safety-cases/${c.id}`, { status, assigned_support: support, action_note: note || undefined, outcome: outcome || undefined }); toast.success("Case updated"); setNote(""); onSaved(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid={`safety-case-${c.id}`} className={`rounded-2xl border bg-white p-5 ${emergency ? "border-red-300 ring-1 ring-red-200" : "border-navy/10"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-heading text-base font-extrabold text-navy">{c.category}</h3>
                        <span className="font-mono text-xs text-navy/40">{c.case_id}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${SEV[c.severity] || SEV.medium}`}>{c.severity}</span>
                    </div>
                    <p className="mt-1 text-sm text-navy/70">{c.description}</p>
                    <p className="mt-1 text-xs text-navy/45">Raised by {c.raised_by} {c.raised_by_name ? `(${c.raised_by_name})` : ""}{c.booking_ref ? ` · ${c.booking_ref}` : ""} · {new Date(c.created_at).toLocaleString("en-IN")}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${SSC[c.status]}`} data-testid={`safety-status-${c.id}`}>{c.status.replace(/_/g, " ")}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-3 text-xs text-navy/60">
                {c.customer_name && <span className="flex items-center gap-1"><User size={13} /> {c.customer_name} {c.customer_phone ? `· ${c.customer_phone}` : ""}</span>}
                {c.professional_name && <span className="flex items-center gap-1"><HardHat size={13} /> {c.professional_name} {c.professional_phone ? `· ${c.professional_phone}` : ""}</span>}
            </div>

            {c.actions?.length > 0 && <div className="mt-3 space-y-1 rounded-xl bg-navy/[0.03] p-3 text-xs text-navy/60">{c.actions.map((a, i) => <p key={i}>• {a.note} <span className="text-navy/35">({new Date(a.at).toLocaleString("en-IN")})</span></p>)}</div>}

            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div>
                    <label className="mb-1 block text-xs font-bold text-navy/60">Status</label>
                    <select data-testid={`safety-status-select-${c.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-red-400">
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-bold text-navy/60">Assigned support</label>
                    <input data-testid={`safety-support-${c.id}`} value={support} onChange={(e) => setSupport(e.target.value)} placeholder="Support agent" className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-red-400" />
                </div>
            </div>
            <input data-testid={`safety-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an action note (contacted, escalated…)" className="mt-2 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-red-400" />
            {["RESOLVED", "CLOSED_WITH_NOTES"].includes(status) && <input data-testid={`safety-outcome-${c.id}`} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Resolution / outcome" className="mt-2 w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-red-400" />}
            <button data-testid={`safety-save-${c.id}`} disabled={busy} onClick={save} className="mt-3 rounded-full bg-navy px-5 py-2 text-xs font-bold text-white disabled:opacity-60">{busy ? "Saving…" : "Update case"}</button>
        </div>
    );
}
